import { getEnrichmentFreshnessSuperuserFreshnessGetOptions } from '@/client/@tanstack/react-query.gen'
import type { FreshnessNodeMetric } from '@/client/types.gen'
import { SkeletonPanels } from '@/components/skeleton-common'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

const categoryOrder: FreshnessNodeMetric['category'][] = [
  'IDENTITY',
  'SOURCE_SYNC',
  'ANALYSIS',
  'ACCOUNT',
]

const categoryLabels: Record<FreshnessNodeMetric['category'], string> = {
  IDENTITY: 'Identity Nodes',
  SOURCE_SYNC: 'Source Sync Nodes',
  ANALYSIS: 'Analysis Nodes',
  ACCOUNT: 'Account Nodes',
}

export const Route = createFileRoute('/_authed/admin/freshness')({
  component: AdminFreshnessPage,
  pendingComponent: () => <SkeletonPanels panels={4} rows={5} columns={5} />,
  errorComponent: () => <p className="text-destructive">Failed to load freshness dashboard.</p>,
  beforeLoad: () => ({
    getTitle: () => 'Enrichment Freshness',
  }),
})

function AdminFreshnessPage() {
  const { data } = useQuery(
    getEnrichmentFreshnessSuperuserFreshnessGetOptions(),
  )

  if (!data) {
    return <></>
  }

  const trackedNodes = data.nodes.filter(node => node.observability === 'TRACKED')
  const totalStaleEntities = trackedNodes.reduce(
    (sum, node) => sum + (node.staleEntities ?? 0),
    0,
  )
  const totalMissingEntities = trackedNodes.reduce(
    (sum, node) => sum + (node.missingEntities ?? 0),
    0,
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Enrichment Freshness</h1>
        <p className="text-muted-foreground">
          Unified view of first-time and periodic enrichment coverage.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Scheduler"
          value={data.periodicScheduler.runTimeUtc}
          subtitle={`${data.periodicScheduler.timezone} · overlap ${data.periodicScheduler.overlapPolicy}`}
        />
        <StatCard
          title="Tracked Nodes"
          value={`${trackedNodes.length}/${data.nodes.length}`}
          subtitle="DB-observable nodes"
        />
        <StatCard
          title="Missing Entities"
          value={formatNumber(totalMissingEntities)}
          subtitle="Eligible entities with no data"
        />
        <StatCard
          title="Stale Entities"
          value={formatNumber(totalStaleEntities)}
          subtitle="Past freshness cutoff"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Cadence Policy</CardTitle>
          <CardDescription>
            <span>Temporal schedule </span>
            <code>{data.periodicScheduler.scheduleId}</code>
            <span> runs once daily and dispatches due cadences.</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.cadencePolicies.map(policy => (
            <div key={policy.cadence} className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">{policy.cadence}</Badge>
              <span className="text-muted-foreground">{policy.dueWhen}</span>
              <span className="text-muted-foreground">
                stale after
                {' '}
                {policy.staleAfterHours === null
                  ? 'n/a'
                  : `${formatHours(policy.staleAfterHours)}`}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>

      {categoryOrder
        .filter(category => data.nodes.some(node => node.category === category))
        .map((category) => {
          const nodes = data.nodes.filter(node => node.category === category)

          return (
            <Card key={category}>
              <CardHeader>
                <CardTitle className="text-lg">{categoryLabels[category]}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Node</TableHead>
                      <TableHead>Cadence</TableHead>
                      <TableHead>Trigger</TableHead>
                      <TableHead>Coverage</TableHead>
                      <TableHead>Missing</TableHead>
                      <TableHead>Stale</TableHead>
                      <TableHead>Latest</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Observability</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nodes.map(node => (
                      <TableRow key={node.nodeName}>
                        <TableCell>
                          <div className="font-medium">{node.displayName}</div>
                          {node.note && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {node.note}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{node.cadence}</Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs">{node.triggerPath}</code>
                        </TableCell>
                        <TableCell>{formatCoverage(node)}</TableCell>
                        <TableCell>{formatNullableNumber(node.missingEntities)}</TableCell>
                        <TableCell>
                          <Badge variant={getStaleVariant(node.staleEntities)}>
                            {formatNullableNumber(node.staleEntities)}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatTimestamp(node.latestRecordAt)}</TableCell>
                        <TableCell>
                          <code className="text-xs">{node.sourceTable}</code>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={node.observability === 'TRACKED' ? 'default' : 'outline'}
                          >
                            {node.observability}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )
        })}
    </div>
  )
}

function StatCard({ title, value, subtitle }: { title: string, value: string, subtitle: string }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">
        {subtitle}
      </CardContent>
    </Card>
  )
}

function formatTimestamp(value: string | null): string {
  if (!value)
    return 'n/a'
  return new Date(value).toLocaleString()
}

function formatHours(hours: number): string {
  if (hours % 24 === 0)
    return `${hours / 24}d`
  return `${hours}h`
}

function formatNumber(value: number): string {
  return value.toLocaleString()
}

function formatNullableNumber(value: number | null): string {
  if (value === null)
    return 'n/a'
  return formatNumber(value)
}

function formatCoverage(node: FreshnessNodeMetric): string {
  if (node.coveragePct === null || node.entitiesWithData === null || node.eligibleEntities === null)
    return 'n/a'
  return `${node.coveragePct}% (${formatNumber(node.entitiesWithData)}/${formatNumber(node.eligibleEntities)})`
}

function getStaleVariant(staleEntities: number | null): 'default' | 'secondary' | 'destructive' {
  if (staleEntities === null)
    return 'secondary'
  if (staleEntities > 0)
    return 'destructive'
  return 'default'
}
